import React, { useState, useEffect, useMemo } from 'react';
import { useData } from '../backend/firebase';
import { RoomAnalyzer } from '../components/HandAnalyzer';
import SEO from '../components/SEO';
import { useWindowSize } from '../hooks/useWindowSize';

// --- HELPER: Derive Contract from BBO Auction ---
const deriveContractFromBBO = (bidding, dealerStr) => {
    if (!bidding || bidding.length === 0) return "Pass";
    const SEATS = ['W', 'N', 'E', 'S'];
    const dealerMap = { '1': 'S', '2': 'W', '3': 'N', '4': 'E' };
    const dealerSeat = dealerMap[dealerStr] || 'S';
    
    let lastBid = null; let lastBidSeat = null; let dbl = '';
    let turn = SEATS.indexOf(dealerSeat) !== -1 ? SEATS.indexOf(dealerSeat) : 3;
    let firstBids = { NS: {}, EW: {} };

    for (let call of bidding) {
        // Strip BBO alerts (e.g. "2C!") so it doesn't break the regex
        const c = call.split('!')[0].toUpperCase().trim();
        const side = (turn === 1 || turn === 3) ? 'NS' : 'EW';
        
        if (c === 'X' || c === 'D') dbl = 'x';
        else if (c === 'XX' || c === 'R') dbl = 'xx';
        else if (c !== 'P' && c !== 'PASS') {
            const match = c.match(/^([1-7])([CDHSN])/);
            if (match) {
                lastBid = { level: match[1], suit: match[2] };
                lastBidSeat = SEATS[turn]; dbl = '';
                if (!firstBids[side][match[2]]) firstBids[side][match[2]] = SEATS[turn];
            }
        }
        turn = (turn + 1) % 4;
    }
    if (!lastBid) return "Pass";
    const winningSide = (lastBidSeat === 'N' || lastBidSeat === 'S') ? 'NS' : 'EW';
    const declarer = firstBids[winningSide][lastBid.suit];
    return `${lastBid.level}${lastBid.suit} ${declarer}${dbl}`;
};



const HandLab = () => {
    const { 
        user, userRole, allUsers,
        createAnalysisBoard, deleteAnalysisBoard, 
        subscribeToAnalysisLobby, updateAnalysisBoard, inviteToAnalysisBoard
    } = useData();
    
    const isAdmin = userRole?.role === 'admin';
    const { width, height } = useWindowSize();
    
    // --- STATE ---
    const [activeBoard, setActiveBoard] = useState(null);
    const [boardId, setBoardId] = useState(null);
    const [liveBoards, setLiveBoards] = useState([]);

    // UI States
    const [boardToDelete, setBoardToDelete] = useState(null);
    const [isSharing, setIsSharing] = useState(false);
    const [shareQuery, setShareQuery] = useState('');

    // Setup Screen State
    const [setupNum, setSetupNum] = useState("1");
    const [setupName, setSetupName] = useState("New Analysis");
    const [bboLink, setBboLink] = useState("");


    const [showSaveModal, setShowSaveModal] = useState(false);
    const [saveName, setSaveName] = useState("");
    const [pendingData, setPendingData] = useState(null);
    const [confirmOverwrite, setConfirmOverwrite] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // --- LOBBY: Fetch available boards ---
    useEffect(() => {
        const unsub = subscribeToAnalysisLobby(user, isAdmin, (snap) => {
            const boards = [];
            snap.forEach(d => boards.push({ id: d.id, ...d.data() }));
            boards.sort((a, b) => (b.lastUpdateTimestamp || 0) - (a.lastUpdateTimestamp || 0));
            setLiveBoards(boards);
        });
        return () => unsub();
    }, [user, isAdmin, subscribeToAnalysisLobby]);

    const labScale = useMemo(() => {
        if (!width || !height) return 1;
        return Math.min((width) / 640, (height) / 600);
    }, [width, height]);

    // --- MANUAL SAVE ---
    const handleManualSave = (linStr, bidding, play, contract) => {
        setPendingData({ linStr, bidding, play, contract });
        setSaveName(activeBoard.boardName);
        setConfirmOverwrite(false);
        setShowSaveModal(true);
    };

    // 2. Executes the actual database write
    const executeSave = async () => {
        setIsSaving(true);
        try {
            const isOverwrite = saveName.trim() === activeBoard.boardName;
            const newState = {
                ...activeBoard, 
                boardName: saveName.trim(),
                details: { 
                    hands: pendingData.linStr || "", 
                    bidding: pendingData.bidding || [], 
                    play: pendingData.play || [], 
                    claim: activeBoard.details?.claim || "" 
                },
                // 🚨 FIX: The key was 'contract', not 'contractStr'! Added fallback just in case.
                contractStr: pendingData.contract || "Pass", 
                lastUpdateTimestamp: Date.now()
            };

            if (isOverwrite && boardId) {
                await updateAnalysisBoard(boardId, { boardState: newState, lastUpdateTimestamp: Date.now() });
                setActiveBoard(newState);
            } else {
                const newId = `board_${Date.now()}`;
                await createAnalysisBoard(newId, { boardState: newState, allowedUsers: [user.email], createdBy: user.email, lastUpdateTimestamp: Date.now() });
                setBoardId(newId); setActiveBoard(newState);
            }
            setShowSaveModal(false);
        } catch (e) { 
            console.error("Firebase Save Error:", e);
            // 🚨 FIX: If it ever fails again, it will tell you exactly what Firebase is complaining about!
            alert(`Save failed: ${e.message}`); 
        } finally {
            setIsSaving(false);
        }
    };

    const handleCreateLocal = () => {
        let initialState = {
            boardNum: setupNum || "1",
            boardName: setupName || "Local Analysis",
            details: { hands: "3S876H876D876C6543,S5432H5432D5432C2,SAKQHAKQDAKQCAKQJ,SJT9HJT9DJT9CT987", bidding: [], play: [], claim: "" },
            contractStr: "Pass",
            aliases: { N: "North", S: "South", E: "East", W: "West" }
        };

        if (bboLink.trim() !== "") {
            const bboData = parseBboLink(bboLink);
            if (bboData) {
                initialState.details = { hands: bboData.hands, bidding: bboData.bidding, play: bboData.play, claim: bboData.claim };
                initialState.aliases = bboData.aliases;
                initialState.contractStr = bboData.contractStr; 
            } else {
                alert("Could not parse BBO link. Starting with a blank board.");
            }
        }

        // By explicitly setting boardId to null, we guarantee it acts purely locally
        setBoardId(null);
        setActiveBoard(initialState);
        setBboLink("");
    };

    // --- BBO PARSER ---
    const parseBboLink = (urlStr) => {
        try {
            const cleanUrl = decodeURIComponent(urlStr.trim());
            const linMatch = cleanUrl.match(/lin=([^&]+)/);
            if (!linMatch) throw new Error("No LIN data found in URL.");
            const lin = linMatch[1];

            // 1. Extract Hands & Dealer (md)
            const mdMatch = lin.match(/md\|([^|]+)\|/);
            let parsedLin = "3S876H876D876C6543,S5432H5432D5432C2,SAKQHAKQDAKQCAKQJ,SJT9HJT9DJT9CT987"; 
            let dealerStr = "3";
            if (mdMatch) {
                const rawMd = mdMatch[1];
                dealerStr = rawMd.charAt(0);
                // FIX: Substring 1 keeps the 'S' for South's Spades!
                parsedLin = dealerStr + rawMd.substring(1); 
            }

            // 2. Extract Bidding (mb) & Play (pc)
            const bidding = [...lin.matchAll(/mb\|([^|]+)\|/g)].map(m => m[1]);
            const play = [...lin.matchAll(/pc\|([^|]+)\|/g)].map(m => m[1]);

            // 3. Extract Claim (mc) - FIX: RoomAnalyzer needs this to trigger the end screen!
            const mcMatch = lin.match(/mc\|([^|]+)\|/);
            const claim = mcMatch ? mcMatch[1] : "";

            // 4. Extract Aliases (pn)
            const aliases = { N: "North", S: "South", E: "East", W: "West" };
            const pnMatch = lin.match(/pn\|([^|]+)\|/);
            if (pnMatch) {
                const names = pnMatch[1].split(',');
                if (names[0]) aliases.S = names[0];
                if (names[1]) aliases.W = names[1];
                if (names[2]) aliases.N = names[2];
                if (names[3]) aliases.E = names[3];
            }

            const contractStr = deriveContractFromBBO(bidding, dealerStr);

            return { hands: parsedLin, bidding, play, claim, aliases, contractStr };
        } catch (error) {
            console.error("BBO Parsing Error:", error);
            return null;
        }
    };

    const handleCreateNew = async () => {
        let initialState = {
            boardNum: setupNum || "1",
            boardName: setupName || "New Analysis",
            details: { hands: "3S876H876D876C6543,S5432H5432D5432C2,SAKQHAKQDAKQCAKQJ,SJT9HJT9DJT9CT987", bidding: [], play: [], claim: "" },
            contractStr: "Pass",
            aliases: { N: "North", S: "South", E: "East", W: "West" }
        };

        if (bboLink.trim() !== "") {
            const bboData = parseBboLink(bboLink);
            if (bboData) {
                // Pass the claim data perfectly into details
                initialState.details = { hands: bboData.hands, bidding: bboData.bidding, play: bboData.play, claim: bboData.claim };
                initialState.aliases = bboData.aliases;
                initialState.contractStr = bboData.contractStr; 
            } else {
                alert("Could not parse BBO link. Starting with a blank board.");
            }
        }

        const newId = `board_${Date.now()}`;
        if (isAdmin) {
            await createAnalysisBoard(newId, {
                boardState: initialState,
                allowedUsers: [user.email],
                createdBy: user.email,
                lastUpdateTimestamp: Date.now()
            });
            setBoardId(newId);
        }
        setActiveBoard(initialState);
        setBboLink("");
    };

    // --- SHARE AUTOCOMPLETE ---
    const filteredUsers = useMemo(() => {
        if (!shareQuery || !allUsers) return [];
        return allUsers.filter(u => 
            u.email?.toLowerCase().includes(shareQuery.toLowerCase()) || 
            (u.name && u.name.toLowerCase().includes(shareQuery.toLowerCase()))
        ).slice(0, 5); 
    }, [shareQuery, allUsers]);

    const executeShare = async (targetEmail) => {
        try {
            await inviteToAnalysisBoard(boardId, targetEmail);
            setShareQuery("");
            setIsSharing(false);
        } catch (e) {
            alert("Failed to share board.");
        }
    };

    return (
        <div className="PageLayout" style={{ height: '100dvh', display: 'flex', flexDirection: 'column' }}>
            <SEO title="Analysis Board" />
            
            <style>{`
                .unified-backdrop {
                    background: #1A1D26; /* Match HandAnalyzer base */
                    border: 1px solid rgba(255,255,255,0.05);
                    box-shadow: 0 15px 40px rgba(0,0,0,0.6);
                    border-radius: 16px;
                    padding: 0px;
                    width: 100%;
                    max-width: 10000px;
                    display: flex;
                    flex-direction: column;
                    gap: 5px;
                }
                .analysis-card {
                    background: rgba(255,255,255,0.02);
                    border: 1px solid rgba(255,255,255,0.04);
                    border-radius: 12px; padding: 25px;
                    display: flex; flex-direction: column; gap: 15px; flex: 1;
                }
                .lobby-grid { display: flex; gap: 20px; align-items: stretch; }
                .board-btn {
                    width: 100%; text-align: left; padding: 12px 16px;
                    background: rgba(0,0,0,0.2); color: #E0E0E0;
                    border: 1px solid rgba(255,255,255,0.05); border-radius: 8px;
                    transition: all 0.2s ease; cursor: pointer;
                    display: flex; flex-direction: column; gap: 4px;
                }
                .board-btn:hover {
                    background: rgba(108, 93, 211, 0.15); border-color: rgba(108, 93, 211, 0.4);
                    transform: translateY(-2px);
                }
                .create-btn {
                    width: 100%; padding: 12px; background: #6C5DD3; color: #fff; 
                    border: none; border-radius: 8px; font-weight: bold; font-size: 14px;
                    cursor: pointer; transition: all 0.2s; text-transform: uppercase;
                }
                .create-btn:hover { background: #5a4bbf; }
                .input-label {
                    display: block; color: #8F9EB5; font-size: 10px; font-weight: bold; 
                    text-transform: uppercase; letter-spacing: 1px; margin-bottom: 6px;
                }
                .delete-modal-overlay {
                    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(0,0,0,0.7); backdrop-filter: blur(3px);
                    z-index: 99999; display: flex; align-items: center; justify-content: center;
                }
                .delete-modal {
                    background: #151821; border: 1px solid rgba(229,62,62,0.3);
                    border-radius: 16px; padding: 30px; width: 320px; text-align: center;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.8);
                }
                .share-dropdown-item {
                    padding: 10px 15px; cursor: pointer; border-bottom: 1px solid rgba(255,255,255,0.05);
                    font-size: 13px; color: #E0E0E0; display: flex; align-items: center; justify-content: space-between;
                }
                .share-dropdown-item:hover { background: rgba(108, 93, 211, 0.2); color: #fff; }
                @media (max-width: 768px) { .lobby-grid { flex-direction: column; } }
            `}</style>

            {/* CUSTOM DELETE MODAL */}
            {boardToDelete && (
                <div className="delete-modal-overlay" onClick={() => setBoardToDelete(null)}>
                    <div className="delete-modal" onClick={e => e.stopPropagation()}>
                        <div style={{ fontSize: 40, marginBottom: 10 }}>🗑️</div>
                        <h2 style={{ margin: '0 0 10px 0', color: '#fff' }}>Delete Board?</h2>
                        <p style={{ color: '#8F9EB5', fontSize: 14, marginBottom: 25 }}>This action cannot be undone. All data will be permanently lost.</p>
                        <div style={{ display: 'flex', gap: 10 }}>
                            <button onClick={() => setBoardToDelete(null)} style={{ flex: 1, padding: 10, background: 'rgba(255,255,255,0.05)', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 'bold' }}>Cancel</button>
                            <button onClick={() => { deleteAnalysisBoard(boardToDelete); setBoardToDelete(null); }} style={{ flex: 1, padding: 10, background: '#E53E3E', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 'bold' }}>Delete</button>
                        </div>
                    </div>
                </div>
            )}

            <div className="ContentWrapper CustomScroll" style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
                
                {/* UNIFIED FIXED-SIZE APP BACKDROP */}
                <div className="unified-backdrop" style={{ maxWidth: activeBoard ? 1800 : 800 , padding: 15}}>
                    
                    {/* HEADER */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                        <h1 style={{ margin: 0, fontSize: 22, color: '#fff', fontWeight: 'bold' }}>
                            {"Analysis Board"}
                        </h1>
                        
                        {activeBoard && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
                                {/* SHARE AUTOCOMPLETE SYSTEM */}
                                {isAdmin &&(
                                    <div style={{ position: 'relative' }}>
                                        {isSharing ? (
                                            <div style={{ display: 'flex', alignItems: 'center', background: '#11131A', border: '1px solid #6C5DD3', borderRadius: 8, padding: '4px 8px' }}>
                                                <input 
                                                    autoFocus placeholder="Search user..." value={shareQuery} onChange={e => setShareQuery(e.target.value)} 
                                                    style={{ border: 'none', background: 'transparent', color: '#fff', width: 140, outline: 'none', fontSize: 13 }} 
                                                />
                                                <button onClick={() => {setIsSharing(false); setShareQuery('');}} style={{ background: 'none', color: '#8F9EB5', border: 'none', cursor: 'pointer' }}>✕</button>
                                                
                                                {shareQuery && (
                                                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 5, background: '#1A1D26', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, overflow: 'hidden', zIndex: 100, boxShadow: '0 10px 25px rgba(0,0,0,0.8)' }}>
                                                        {filteredUsers.length > 0 ? (
                                                            filteredUsers.map(u => (
                                                                <div key={u.uid} className="share-dropdown-item" onClick={() => executeShare(u.email)}>
                                                                    <span style={{fontWeight: 'bold'}}>{u.name || u.email.split('@')[0]}</span>
                                                                    <span style={{color: '#8F9EB5', fontSize: 11}}>Invite</span>
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <div style={{ padding: '10px', color: '#8F9EB5', fontSize: 12, textAlign: 'center' }}>No matches found.</div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <button onClick={() => setIsSharing(true)} style={{ background: 'transparent', color: '#A0AABF', border: '1px solid rgba(255,255,255,0.2)', padding: '6px 12px', borderRadius: 6, fontWeight: 'bold', cursor: 'pointer', fontSize: 12 }}>🔗 Share</button>
                                        )}
                                    </div>
                                )}


                                <div style={{ position: 'relative' }}>
                                    {isAdmin && (
                                        <button 
                                            onClick={() => document.getElementById('hidden-save-btn')?.click()} 
                                            style={{ background: 'rgba(74, 222, 128, 0.15)', color: '#4ADE80', border: '1px solid rgba(74, 222, 128, 0.3)', padding: '6px 12px', borderRadius: 6, fontWeight: 'bold', cursor: 'pointer', fontSize: 12 }}
                                        >
                                            💾 Save
                                        </button>
                                    )}

                                    {showSaveModal && (
                                        <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 8, background: '#1A1D26', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: 20, width: 260, zIndex: 100000, boxShadow: '0 15px 35px rgba(0,0,0,0.8)' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 15 }}>
                                                <h3 style={{ margin: 0, fontSize: 16, color: '#fff' }}>Save Board</h3>
                                                <button onClick={() => setShowSaveModal(false)} style={{ background: 'none', border: 'none', color: '#8F9EB5', cursor: 'pointer' }}>✕</button>
                                            </div>
                                            
                                            <input className="DarkInput" value={saveName} onChange={e => {setSaveName(e.target.value); setConfirmOverwrite(false);}} style={{width: '100%', marginBottom: 15, margin: 0}} />
                                            
                                            {saveName.trim() === activeBoard.boardName ? (
                                                confirmOverwrite ? (
                                                    <div style={{ background: 'rgba(229, 62, 62, 0.1)', padding: 10, borderRadius: 8, border: '1px solid rgba(229, 62, 62, 0.3)', textAlign: 'center' }}>
                                                        <div style={{ fontSize: 12, color: '#E53E3E', marginBottom: 10, fontWeight: 'bold' }}>Overwrite original?</div>
                                                        <div style={{ display: 'flex', gap: 8 }}>
                                                            <button onClick={() => setConfirmOverwrite(false)} style={{ flex: 1, padding: '6px', background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
                                                            <button onClick={executeSave} disabled={isSaving} style={{ flex: 1, padding: '6px', background: '#E53E3E', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 'bold' }}>{isSaving ? '...' : 'Yes'}</button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <button onClick={() => setConfirmOverwrite(true)} style={{ width: '100%', padding: '10px', background: 'transparent', color: '#E0E0E0', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, cursor: 'pointer', fontWeight: 'bold' }}>Overwrite</button>
                                                )
                                            ) : (
                                                <button onClick={executeSave} disabled={isSaving} style={{ width: '100%', padding: '10px', background: '#6C5DD3', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 'bold' }}>Save as New</button>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <button onClick={() => {setActiveBoard(null); setBoardId(null); setIsSharing(false);}} style={{ background: 'rgba(229, 62, 62, 0.15)', color: '#E53E3E', border: '1px solid rgba(229, 62, 62, 0.3)', padding: '6px 12px', borderRadius: 6, fontWeight: 'bold', cursor: 'pointer', fontSize: 12 }}>Close</button>
                            </div>
                        )}
                    </div>

                    {!activeBoard ? (
                        <div className="lobby-grid">
                            {/* LEFT: CREATE */}
                            <div className="analysis-card">
                                <div>
                                    <h2 style={{ margin: 0, fontSize: 18, color: '#fff' }}>Start New</h2>
                                    <p style={{ margin: '4px 0 0 0', color: '#8F9EB5', fontSize: 12 }}>Construct a new board or import from BBO.</p>
                                </div>
                                
                                <div style={{ display: 'flex', gap: 15 }}>
                                    <div style={{ flex: 1 }}>
                                        <label className="input-label">Board #</label>
                                        <input className="DarkInput" placeholder="e.g. 1" value={setupNum} onChange={e => setSetupNum(e.target.value)} style={{ width: '100%', margin: 0 }} />
                                    </div>
                                    <div style={{ flex: 2 }}>
                                        <label className="input-label">Name</label>
                                        <input className="DarkInput" placeholder="e.g. Defending 3NT" value={setupName} onChange={e => setSetupName(e.target.value)} style={{ width: '100%', margin: 0 }} />
                                    </div>
                                </div>

                                <div>
                                    <label className="input-label">BBO Handviewer Link <span style={{fontWeight: 'normal', color: '#666'}}>(Optional)</span></label>
                                    <input className="DarkInput" placeholder="https://www.bridgebase.com/tools/..." value={bboLink} onChange={e => setBboLink(e.target.value)} style={{ width: '100%', margin: 0 }} />
                                </div>
                                
                                <div style={{ marginTop: 'auto', paddingTop: 10, display: 'flex', gap: 10 }}>
                                    {isAdmin && (
                                        <button className="create-btn" onClick={handleCreateNew} style={{ flex: 1 }}>
                                            Save to Cloud
                                        </button>
                                    )}
                                    <button 
                                        className="create-btn" 
                                        onClick={handleCreateLocal} 
                                        style={{ flex: 1, background: 'transparent', border: '1px solid #6C5DD3', color: '#A395E9' }}
                                    >
                                        Play Locally
                                    </button>
                                </div>
                            </div>

                            {/* RIGHT: LOAD */}
                            <div className="analysis-card">
                                <div>
                                    <h2 style={{ margin: 0, fontSize: 18, color: '#E0E0E0' }}>Load Board</h2>
                                    <p style={{ margin: '4px 0 0 0', color: '#8F9EB5', fontSize: 12 }}>Access your saved sessions.</p>
                                </div>

                                {liveBoards.length === 0 ? (
                                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#555', fontStyle: 'italic', fontSize: 13 }}>
                                        No saved boards found.
                                    </div>
                                ) : (
                                    <div className="CustomScroll" style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, maxHeight: 240, overflowY: 'auto', paddingRight: 5 }}>
                                        {liveBoards.map(b => (
                                            <div key={b.id} style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                                                <button className="board-btn" onClick={() => {setBoardId(b.id); setActiveBoard(b.boardState);}}>
                                                    <span style={{ fontWeight: 'bold', fontSize: 14 }}>{b.boardState.boardName}</span>
                                                    <span style={{ fontSize: 11, color: '#8F9EB5' }}>Board {b.boardState.boardNum} • By {b.createdBy?.split('@')[0]}</span>
                                                </button>
                                                {isAdmin && (
                                                    <button onClick={() => setBoardToDelete(b.id)} style={{ background: 'rgba(229, 62, 62, 0.1)', color: '#E53E3E', border: '1px solid rgba(229, 62, 62, 0.3)', padding: '0 12px', borderRadius: 8, cursor: 'pointer', fontWeight: 'bold' }}>✕</button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        /* ROOM ANALYZER EMBED: Perfect prop pass-through */
                        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 0 }}>
                            <RoomAnalyzer 
                                details={activeBoard.details} 
                                roomName="Analysis Board" 
                                isActive={true} 
                                contractStr={activeBoard.contractStr} 
                                rawScore={0} 
                                boardNum={activeBoard.boardNum} 
                                aliases={activeBoard.aliases} 
                                displayMode="visual" 
                                hidePlayed={true}
                                showFullAuction={false}
                                hideAuctionInPlay={false}
                                isFullscreen={true} 
                                scale={labScale}
                                onRoomDD={true}
                                onSaveLabHand={handleManualSave}
                                cardScale={1.4}
                                tableScale={1.3}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default HandLab;